import { Injectable, inject } from '@angular/core';
import { 
  Firestore, 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs, 
  serverTimestamp, 
  addDoc, 
  collectionData,
  deleteDoc,
  runTransaction
} from '@angular/fire/firestore';
import { Observable, combineLatest, map, of } from 'rxjs';
import { AuthService } from './auth.service';
import { ProjectIdService } from './project-id.service';
import { ApprovalRequest, ApprovalHistory, ApproverMapping } from '../models/approval.model';

@Injectable({ providedIn: 'root' })
export class ApprovalService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private projectIdService = inject(ProjectIdService);

  /**
   * Centralized approval-queue collection. Renamed from `approval_queue` to
   * `pending_approvals` to align with the v2 spec (Firestore is the single
   * source of truth for the create / edit / delete workflow).
   */
  private readonly QUEUE_COLLECTION = 'pending_approvals';
  private readonly LEGACY_QUEUE_COLLECTION = 'approval_queue';   // backward-compat reads
  private readonly MAPPING_COLLECTION = 'approver_mapping';
  private readonly HISTORY_COLLECTION = 'approval_history';

  /** Enqueue a change request and compute target approvers */
  async enqueueChange(request: Partial<ApprovalRequest>): Promise<void> {
    const operator = this.authService.currentUser();
    if (!operator) throw new Error('User not authenticated');

    const approverIds = await this.enrichApproverIds(await this.computeApprovers(request));

    // Snapshot existing live data so the approver can see oldData vs newData.
    let oldData: any = null;
    if (request.targetCollection && request.targetDocId &&
        (request.operation === 'update' || request.operation === 'delete')) {
      try { oldData = await this.getTargetData(request.targetCollection, request.targetDocId); }
      catch { /* live doc may not exist yet — fine */ }
    }

    const deptId = this.normalizeDeptId(String(
      request.payload?.dept ?? request.payload?.departmentId ?? request.payload?.deptId ?? ''
    ));

    // Spec-aligned uppercase request type
    const requestType: 'CREATE' | 'EDIT' | 'DELETE' =
      request.operation === 'create' ? 'CREATE'
    : request.operation === 'delete' ? 'DELETE'
    :                                  'EDIT';

    const data: any = {
      // ── Spec-aligned fields (v2) ─────────────────────────────────────
      requestType,                                    // 'CREATE' | 'EDIT' | 'DELETE'
      approvalStatus: 'PENDING',                      // 'PENDING' | 'APPROVED' | 'REJECTED'
      submittedBy: operator.uid,                      // operatorUid alias
      department: deptId || null,                     // routes to dept approvers
      submittedAt: serverTimestamp(),
      updatedByRole: operator.role ?? null,           // track approver-submitted edits

      // Per-request payloads (matches the doc shape requested in the spec)
      projectData:        requestType === 'CREATE' ? request.payload : null,
      updatedData:        requestType === 'EDIT'   ? request.payload : null,
      originalProjectId:  requestType === 'EDIT'   ? (request.targetDocId || null) : null,
      projectId:          requestType === 'DELETE' ? (request.targetDocId || null) : null,

      // ── Legacy fields kept for in-flight workflows / older clients ──
      operatorUid: operator.uid,
      targetCollection: request.targetCollection!,
      targetDocId: request.targetDocId || null,
      operation: request.operation!,
      payload: request.payload,
      newData: request.payload,
      oldData: oldData,
      status: 'PENDING',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      approverIds,
      remarks: request.remarks || ''
    };

    // Safety: Remove any undefined fields that Firestore would reject
    Object.keys(data).forEach(key => data[key] === undefined && delete data[key]);

    const coll = collection(this.firestore, this.QUEUE_COLLECTION);
    await addDoc(coll, data);

    // Log initial history entry
    const historyData: any = {
      requestId: 'initial',
      operatorUid: operator.uid,
      approverUid: 'SYSTEM',
      targetCollection: request.targetCollection!,
      targetDocId: request.targetDocId || null,
      operation: request.operation!,
      beforeData: oldData,
      afterData: request.payload,
      status: 'PENDING',
      remarks: 'Request submitted for approval',
      timestamp: serverTimestamp()
    };

    Object.keys(historyData).forEach(key => historyData[key] === undefined && delete historyData[key]);
    await addDoc(collection(this.firestore, this.HISTORY_COLLECTION), historyData);
  }

  /**
   * Compute approver UIDs for a request.
   *
   * Routing precedence (first non-empty wins):
   *  1. Approvers whose `users.departmentIds` includes the project's normalized dept ID
   *     (this is the canonical mapping built from the User Management screen).
   *  2. Optional `approver_mapping` rules (legacy / power-user overrides).
   *  3. Fallback: any user with role === 'approver' (so requests don't dead-end).
   *  4. Last resort: any admin.
   */
  private async computeApprovers(request: Partial<ApprovalRequest>): Promise<string[]> {
    const matches = new Set<string>();
    const operatorUid = this.authService.currentUser()?.uid;
    const deptRaw = request.payload?.departmentId
                 ?? request.payload?.deptId
                 ?? request.payload?.dept
                 ?? '';
    const deptId = this.normalizeDeptId(String(deptRaw));

    // ── 1. Department-routed approvers via users.departmentIds ───────────
    if (deptId) {
      try {
        const usersColl = collection(this.firestore, 'users');
        const snap = await getDocs(query(usersColl, where('role', '==', 'approver')));
        snap.forEach(d => {
          const ids: string[] = (d.data()['departmentIds'] as string[] | undefined) || [];
          const hit = ids.some(id => this.normalizeDeptId(String(id)) === deptId);
          if (hit) matches.add(d.id);
        });
      } catch (err) {
        console.warn('[approval] dept-routed approver lookup failed:', err);
      }
    }

    // ── 2. Optional approver_mapping overrides ───────────────────────────
    try {
      const coll = collection(this.firestore, this.MAPPING_COLLECTION);
      const globalSnap = await getDocs(query(coll, where('scope', '==', 'GLOBAL')));
      globalSnap.forEach(d => matches.add((d.data() as ApproverMapping).approverUid));

      if (operatorUid) {
        const opSnap = await getDocs(query(coll, where('scope', '==', 'OPERATOR'), where('operatorUid', '==', operatorUid)));
        opSnap.forEach(d => matches.add((d.data() as ApproverMapping).approverUid));
      }
      if (deptId) {
        const deptSnap = await getDocs(query(coll, where('scope', '==', 'DEPARTMENT'), where('departmentId', '==', deptId)));
        deptSnap.forEach(d => matches.add((d.data() as ApproverMapping).approverUid));
      }
    } catch {
      // approver_mapping is optional — silent fail is fine
    }

    // ── 3. Fallback: any approver in the system ──────────────────────────
    if (matches.size === 0) {
      try {
        const usersColl = collection(this.firestore, 'users');
        const anyApproverSnap = await getDocs(query(usersColl, where('role', '==', 'approver')));
        anyApproverSnap.forEach(d => matches.add(d.id));
      } catch (err) {
        console.warn('[approval] approver fallback lookup failed:', err);
      }
    }

    // ── 4. Last resort: admins ───────────────────────────────────────────
    if (matches.size === 0) {
      try {
        const usersColl = collection(this.firestore, 'users');
        const adminSnap = await getDocs(query(usersColl, where('role', '==', 'admin')));
        adminSnap.forEach(d => matches.add(d.id));
      } catch (err) {
        console.error('[approval] CRITICAL: no approvers and admin lookup failed:', err);
      }
    }

    return Array.from(matches);
  }

  /**
   * When writing approverIds, also include Firebase Auth UIDs so queue reads
   * work when profile doc id differs from auth uid (legacy accounts).
   */
  private async enrichApproverIds(profileIds: string[]): Promise<string[]> {
    const enriched = new Set(profileIds);
    const authUid = this.authService.getFirebaseAuthUid();
    if (authUid) enriched.add(authUid);
    try {
      const usersColl = collection(this.firestore, 'users');
      await Promise.all(profileIds.map(async profileId => {
        const snap = await getDoc(doc(usersColl, profileId));
        if (!snap.exists()) return;
        const email = String(snap.data()?.['email'] || '').trim().toLowerCase();
        if (!email) return;
        const byEmail = await getDocs(query(usersColl, where('email', '==', email)));
        byEmail.forEach(d => enriched.add(d.id));
      }));
    } catch (err) {
      console.warn('[approval] enrichApproverIds failed:', err);
    }
    return Array.from(enriched);
  }

  /** Normalise a department display name to the same ID used in users.departmentIds. */
  private normalizeDeptId(name: string): string {
    return (name || '')
      .toLowerCase()
      .replace(/[\s&]/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_');
  }

  /**
   * Live stream of every approval-queue row routed to the current approver
   * (PENDING, APPROVED, REJECTED). Used to keep the department table in sync
   * immediately after approve/reject — not only while requests are pending.
   */
  getApproverRequests(): Observable<ApprovalRequest[]> {
    const user = this.authService.currentUser();
    if (!user) return of([]);

    const authUid = this.authService.getFirebaseAuthUid();
    const candidateIds = Array.from(
      new Set(
        [user.uid, authUid].filter((id): id is string => typeof id === 'string' && id.length > 0)
      )
    );

    const mergeCollection = (collName: string): Observable<ApprovalRequest[]> => {
      const coll = collection(this.firestore, collName);
      if (user.role === 'admin') {
        return collectionData(query(coll), { idField: 'id' }) as Observable<ApprovalRequest[]>;
      }
      const streams = candidateIds.map(id =>
        collectionData(
          query(coll, where('approverIds', 'array-contains', id)),
          { idField: 'id' }
        ) as Observable<ApprovalRequest[]>
      );
      if (streams.length === 0) return of([]);
      if (streams.length === 1) return streams[0];
      return combineLatest(streams).pipe(
        map(groups => {
          const byId = new Map<string, ApprovalRequest>();
          for (const group of groups) {
            for (const r of group) {
              const rid = (r as any)?.id;
              if (rid) byId.set(rid, r as ApprovalRequest);
            }
          }
          return Array.from(byId.values());
        })
      );
    };

    const main$   = mergeCollection(this.QUEUE_COLLECTION);
    const legacy$ = mergeCollection(this.LEGACY_QUEUE_COLLECTION);

    return combineLatest([main$, legacy$]).pipe(
      map(([a, b]) => {
        const byId = new Map<string, ApprovalRequest>();
        for (const r of [...a, ...b]) {
          const id = (r as any)?.id;
          if (id) byId.set(id, r as ApprovalRequest);
        }
        return Array.from(byId.values());
      })
    );
  }

  /**
   * Get pending requests for the current approver. Merges live snapshots
   * from both `pending_approvals` (new) and `approval_queue` (legacy in-flight)
   * so renaming the collection doesn't strand existing requests.
   */
  getPendingRequests(): Observable<ApprovalRequest[]> {
    const user = this.authService.currentUser();
    if (!user) return of([]);

    /**
     * Backward/forward compatibility:
     * Some deployments used `approvalStatus` (spec field) while older ones used `status`.
     * Firestore cannot OR-query across two fields, so we merge two live listeners and de-dupe.
     */
    const buildQueries = (collName: string, statusField: 'status' | 'approvalStatus') => {
      const coll = collection(this.firestore, collName);
      if (user.role === 'admin') {
        return [query(coll, where(statusField, '==', 'PENDING'))];
      }
      const authUid = this.authService.getFirebaseAuthUid();
      const candidateIds = Array.from(
        new Set(
          [user.uid, authUid].filter((id): id is string => typeof id === 'string' && id.length > 0)
        )
      );
      return candidateIds.map(id =>
        query(coll, where('approverIds', 'array-contains', id), where(statusField, '==', 'PENDING'))
      );
    };

    const mergePending = (queries: ReturnType<typeof query>[]) => {
      if (queries.length === 0) return of([] as ApprovalRequest[]);
      const streams = queries.map(q =>
        collectionData(q as any, { idField: 'id' }) as Observable<ApprovalRequest[]>
      );
      if (streams.length === 1) return streams[0];
      return combineLatest(streams).pipe(
        map(groups => {
          const byId = new Map<string, ApprovalRequest>();
          for (const group of groups) {
            for (const r of group) {
              const id = (r as any)?.id;
              if (id) byId.set(id, r as ApprovalRequest);
            }
          }
          return Array.from(byId.values());
        })
      );
    };

    const mainStatus$   = mergePending(buildQueries(this.QUEUE_COLLECTION, 'status'));
    const mainSpec$     = mergePending(buildQueries(this.QUEUE_COLLECTION, 'approvalStatus'));
    const legacyStatus$ = mergePending(buildQueries(this.LEGACY_QUEUE_COLLECTION, 'status'));
    const legacySpec$   = mergePending(buildQueries(this.LEGACY_QUEUE_COLLECTION, 'approvalStatus'));

    return combineLatest([mainStatus$, mainSpec$, legacyStatus$, legacySpec$]).pipe(
      map(([a, b, c, d]) => {
        const all = [...a, ...b, ...c, ...d];
        const byId = new Map<string, ApprovalRequest>();
        for (const r of all) {
          const id = (r as any)?.id;
          if (!id) continue;
          byId.set(id, r);
        }
        return Array.from(byId.values());
      })
    );
  }

  /** Pending-only view — derived from {@link getApproverRequests}. */
  getApproverPendingRequests(): Observable<ApprovalRequest[]> {
    return this.getApproverRequests().pipe(
      map(requests => requests.filter(r => {
        const s = String((r as any).status ?? (r as any).approvalStatus ?? '').toUpperCase();
        return s === 'PENDING' || s === '';
      }))
    );
  }

  /**
   * Get requests submitted by the current operator (merges both queues).
   * Queries every candidate uid (Firestore profile id + Firebase Auth uid) because
   * legacy rows may store `operatorUid` as either value; a single-uid query misses
   * data after refresh when the hydrated profile id differs from auth uid.
   */
  getOperatorRequests(): Observable<ApprovalRequest[]> {
    const user = this.authService.currentUser();
    if (!user) return of([]);

    const authUid = this.authService.getFirebaseAuthUid();
    const candidateIds = Array.from(
      new Set(
        [user.uid, authUid].filter((id): id is string => typeof id === 'string' && id.length > 0)
      )
    );

    const mergeCollection = (collName: string): Observable<ApprovalRequest[]> => {
      const coll = collection(this.firestore, collName);
      const streams = candidateIds.map(id =>
        collectionData(query(coll, where('operatorUid', '==', id)), { idField: 'id' }) as Observable<ApprovalRequest[]>
      );
      if (streams.length === 0) {
        return of([]);
      }
      if (streams.length === 1) {
        return streams[0];
      }
      return combineLatest(streams).pipe(
        map(groups => {
          const byId = new Map<string, ApprovalRequest>();
          for (const group of groups) {
            for (const r of group) {
              const id = (r as any).id;
              if (id) {
                byId.set(id, r as ApprovalRequest);
              }
            }
          }
          return Array.from(byId.values());
        })
      );
    };

    const main$   = mergeCollection(this.QUEUE_COLLECTION);
    const legacy$ = mergeCollection(this.LEGACY_QUEUE_COLLECTION);

    return combineLatest([main$, legacy$]).pipe(
      map(([a, b]) => {
        const byId = new Map<string, ApprovalRequest>();
        for (const r of [...a, ...b]) {
          const id = (r as any).id;
          if (id) {
            byId.set(id, r as ApprovalRequest);
          }
        }
        return Array.from(byId.values());
      })
    );
  }

  /**
   * Resolve a request doc id to its actual Firestore reference, looking in
   * both the new `pending_approvals` collection and the legacy `approval_queue`.
   */
  private async resolveRequestRef(requestId: string) {
    let reqRef = doc(this.firestore, this.QUEUE_COLLECTION, requestId);
    let reqSnap = await getDoc(reqRef);
    if (!reqSnap.exists()) {
      reqRef = doc(this.firestore, this.LEGACY_QUEUE_COLLECTION, requestId);
      reqSnap = await getDoc(reqRef);
    }
    if (!reqSnap.exists()) throw new Error('Request not found');
    return { reqRef, reqSnap };
  }

  /**
   * Approve a pending request and apply changes to the target collection.
   *
   * @param requestId       id of the pending_approvals (or legacy approval_queue) doc
   * @param remarks         optional reviewer note
   * @param editedPayload   when provided, the approver's edited values replace
   *                        the operator's original payload before being written
   *                        to the live target collection. Only top-level keys
   *                        present in the override are changed.
   */
  async approveRequest(
    requestId: string,
    remarks: string = '',
    editedPayload?: Record<string, any> | null
  ): Promise<void> {
    const approver = this.authService.currentUser();
    if (!approver) throw new Error('Not authenticated');

    const { reqRef, reqSnap } = await this.resolveRequestRef(requestId);
    const request = reqSnap.data() as ApprovalRequest;
    const targetColl = collection(this.firestore, request.targetCollection);

    // Merge the approver's edits over the operator's original payload, dropping
    // undefined values that Firestore would reject.
    const basePayload = (request as any).newData ?? request.payload ?? {};
    const finalPayload: Record<string, any> = editedPayload
      ? { ...basePayload, ...editedPayload }
      : { ...basePayload };
    Object.keys(finalPayload).forEach(k => finalPayload[k] === undefined && delete finalPayload[k]);
    const wasEdited = !!editedPayload && Object.keys(editedPayload).length > 0;

    if (
      request.operation === 'create' &&
      request.targetCollection === 'projects' &&
      !finalPayload['projectId']
    ) {
      const idResult = await this.projectIdService.reserveNextProjectId(finalPayload['dept']);
      finalPayload['projectId']      = idResult.projectId;
      finalPayload['departmentCode'] = idResult.departmentCode;
      finalPayload['sequenceNumber'] = idResult.sequenceNumber;
    }

    await runTransaction(this.firestore, async (transaction) => {
      // Handle bulk project import
      if (request.operation === 'create' && request.payload?.projects) {
        // Bulk import: create each project individually
        const projects = request.payload.projects;
        const createdProjectIds: string[] = [];
        
        for (const project of projects) {
          const projectData = {
            name: project.name,
            village: project.village,
            block: project.block || 'Dhamanagar',
            scheme: project.scheme,
            cost: parseFloat(project.cost) || 0,
            spent: 0,
            physical: 0,
            status: 'Planned',
            start: project.start || '',
            end: project.end || '',
            priority: project.priority || false,
            remarks: project.remarks || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            dept: project.dept || project.village,
            loc: project.village,
            sanctionedYear: project.year || ''
          };

          const newDocRef = doc(targetColl);
          await transaction.set(newDocRef, projectData);
          createdProjectIds.push(newDocRef.id);
        }

        // Create history entries for bulk import
        for (let i = 0; i < projects.length; i++) {
          const historyRef = doc(collection(this.firestore, this.HISTORY_COLLECTION));
          const history: ApprovalHistory = {
            requestId: `${requestId}_bulk_${i}`,
            operatorUid: request.operatorUid,
            approverUid: approver.uid,
            targetCollection: request.targetCollection,
            targetDocId: createdProjectIds[i],
            operation: 'create',
            beforeData: null,
            afterData: projects[i],
            status: 'APPROVED',
            remarks: `Bulk import - Project ${i + 1}: ${projects[i].name}`,
            timestamp: serverTimestamp()
          };
          transaction.set(historyRef, history);
        }

        // Update the request status
        const queueUpdate: Record<string, any> = {
          status: 'APPROVED',
          approvalStatus: 'APPROVED',
          approverUid: approver.uid,
          approverId: approver.uid,
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          remarks: `Bulk import approved: ${projects.length} projects`,
          bulkImportCount: projects.length
        };
        transaction.update(reqRef, queueUpdate);

      } else {
        // Handle single project operations (original logic)
        // 1. Get before Data if it's an update/delete
        let beforeData = null;
        if (request.targetDocId && (request.operation === 'update' || request.operation === 'delete')) {
          const targetRef = doc(this.firestore, request.targetCollection, request.targetDocId);
          const targetSnap = await transaction.get(targetRef);
          beforeData = targetSnap.exists() ? targetSnap.data() : null;
        }

        // 2. Apply the (possibly edited) payload to the target collection
        //    Track the live doc id so later Edit / Delete from the history view
        //    can reference the newly-created project.
        let appliedDocId: string | null = request.targetDocId || null;

        if (request.operation === 'create') {
          const newDocRef = doc(targetColl);
          appliedDocId = newDocRef.id;
          transaction.set(newDocRef, { ...finalPayload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
        } else if (request.operation === 'update' && request.targetDocId) {
          const targetRef = doc(this.firestore, request.targetCollection, request.targetDocId);
          transaction.update(targetRef, { ...finalPayload, updatedAt: serverTimestamp() });
        } else if (request.operation === 'delete' && request.targetDocId) {
          const targetRef = doc(this.firestore, request.targetCollection, request.targetDocId);
          transaction.delete(targetRef);
        }

        // 3. Move to history (records both the original and the actually-applied payload)
        const historyRef = doc(collection(this.firestore, this.HISTORY_COLLECTION));
        const history: ApprovalHistory & { editedByApprover?: boolean; originalPayload?: any } = {
          requestId,
          operatorUid: request.operatorUid,
          approverUid: approver.uid,
          targetCollection: request.targetCollection,
          targetDocId: appliedDocId ?? undefined,        // ← created/edited doc id (CREATE path now captured)
          operation: request.operation,
          beforeData,
          afterData: finalPayload,
          originalPayload: wasEdited ? basePayload : undefined,
          editedByApprover: wasEdited,
          status: 'APPROVED',
          remarks,
          timestamp: serverTimestamp()
        };
        // Drop undefined fields Firestore would reject
        Object.keys(history).forEach(k => (history as any)[k] === undefined && delete (history as any)[k]);
        transaction.set(historyRef, history);

        // 4. Update queue status (legacy `status` + spec-aligned `approvalStatus`)
        const queueUpdate: Record<string, any> = {
          status: 'APPROVED',
          approvalStatus: 'APPROVED',
          approverUid: approver.uid,
          approverId: approver.uid,                     // spec alias
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          remarks,
        };
        if (appliedDocId) {
          // For CREATE: persist the resulting project id so later edits can target it.
          queueUpdate['createdDocId'] = appliedDocId;
          if (request.operation === 'create' && !request.targetDocId) {
            queueUpdate['targetDocId'] = appliedDocId;
          }
        }
        if (wasEdited) {
          queueUpdate['approvedPayload']  = finalPayload;
          queueUpdate['editedByApprover'] = true;
        }
        transaction.update(reqRef, queueUpdate);
      }
    });
  }

  /**
   * Operator-initiated withdrawal of their OWN request. Deletes the queue doc
   * (works against both `pending_approvals` and legacy `approval_queue`).
   * The Firestore rules permit this when the doc's `operatorUid` matches the
   * caller and the status is still PENDING or REJECTED (no point withdrawing
   * an already-approved request — the live doc exists).
   */
  async withdrawRequest(requestId: string): Promise<void> {
    const user = this.authService.currentUser();
    if (!user) throw new Error('Not authenticated');

    const { reqRef, reqSnap } = await this.resolveRequestRef(requestId);
    const data = reqSnap.data() as ApprovalRequest;

    if (data.operatorUid !== user.uid) {
      throw new Error('You can only withdraw your own requests.');
    }
    if (data.status !== 'PENDING' && data.status !== 'REJECTED') {
      throw new Error('Only pending or rejected requests can be withdrawn.');
    }

    // Log a history entry so the audit trail still shows the withdrawal.
    try {
      await addDoc(collection(this.firestore, this.HISTORY_COLLECTION), {
        requestId,
        operatorUid: user.uid,
        approverUid: 'SYSTEM',
        targetCollection: data.targetCollection,
        targetDocId: data.targetDocId ?? null,
        operation: data.operation,
        beforeData: (data as any).oldData ?? null,
        afterData: data.payload ?? null,
        status: 'REJECTED',
        remarks: 'Withdrawn by operator',
        timestamp: serverTimestamp(),
      });
    } catch (err) {
      console.warn('[approval] withdraw history log failed:', err);
    }

    await deleteDoc(reqRef);
  }

  /** Reject a pending request */
  async rejectRequest(requestId: string, remarks: string): Promise<void> {
    const approver = this.authService.currentUser();
    if (!approver) throw new Error('Not authenticated');

    const { reqRef, reqSnap } = await this.resolveRequestRef(requestId);
    const request = reqSnap.data() as ApprovalRequest;

    await runTransaction(this.firestore, async (transaction) => {
      // 1. Update queue status (legacy `status` + spec-aligned `approvalStatus`)
      transaction.update(reqRef, {
        status: 'REJECTED',
        approvalStatus: 'REJECTED',
        approverUid: approver.uid,
        approverId: approver.uid,                     // spec alias
        rejectedAt: serverTimestamp(),
        rejectionComment: remarks,
        rejectionReason: remarks,                     // spec alias
        updatedAt: serverTimestamp(),
        remarks,
      });

      // 2. Log to history
      const historyRef = doc(collection(this.firestore, this.HISTORY_COLLECTION));
      const history: ApprovalHistory = {
        requestId,
        operatorUid: request.operatorUid,
        approverUid: approver.uid,
        targetCollection: request.targetCollection,
        targetDocId: request.targetDocId,
        operation: request.operation,
        afterData: request.payload,
        status: 'REJECTED',
        remarks,
        timestamp: serverTimestamp()
      };
      transaction.set(historyRef, history);
    });
  }

  /** Get the current data of a target document for comparison */
  async getTargetData(collectionName: string, docId: string): Promise<any> {
    if (!docId) return null;
    const docRef = doc(this.firestore, collectionName, docId);
    const snap = await getDoc(docRef);
    return snap.exists() ? snap.data() : null;
  }

  /** Get real-time stream of audit logs for the current user */
  getAuditLogs(): Observable<ApprovalHistory[]> {
    const user = this.authService.currentUser();
    if (!user) return of([]);
    
    const coll = collection(this.firestore, this.HISTORY_COLLECTION);
    // Filter for logs where user was either operator or approver
    // Since Firestore doesn't support OR on different fields easily without complex queries,
    // we'll fetch where they were either, or just their role-specific ones.
    // Given security rules likely restrict by role/uid, we use a query compatible with standard per-user rules.
    const q = user.role === 'admin' 
      ? query(coll) // Admins can usually see all
      : query(coll, where(user.role === 'operator' ? 'operatorUid' : 'approverUid', '==', user.uid));

    return collectionData(q, { idField: 'id' }).pipe(
      map(logs => (logs as ApprovalHistory[]).sort((a: any, b: any) => {
        const tA = a.timestamp?.seconds || 0;
        const tB = b.timestamp?.seconds || 0;
        return tB - tA;
      }))
    );
  }
}
